import { AuthenticationContext } from '@upside/spa-shared-context';

import { paths } from 'shell/const';

import { getApplicationContext } from '../shell/application-context';

function isValidRedirectDomain(
  url: string,
  authenticationContext: AuthenticationContext,
): boolean {
  try {
    const { hostname, host, protocol } = new URL(url);

    if (protocol !== 'https:' && hostname !== 'localhost') {
      return false;
    }

    const allowedDomains = authenticationContext.allowedRedirectDomains ?? [];

    return allowedDomains.some((allowed) => {
      if (hostname === 'localhost') {
        return host === allowed;
      }

      if (hostname === allowed) return true;

      return hostname.endsWith(`.${allowed}`);
    });
  } catch {
    return false;
  }
}

function buildLoginRedirectUrl(
  originalRedirect: string | null,
  params: URLSearchParams,
): string {
  const loginRedirectParams = new URLSearchParams(params);

  if (originalRedirect) {
    loginRedirectParams.set('redirectTo', `/${paths.REDIRECT}`);
  } else {
    loginRedirectParams.delete('origin');
  }

  const loginRedirectParamsString = loginRedirectParams.toString();
  return loginRedirectParamsString ? `?${loginRedirectParamsString}` : '';
}

export function handleExternalRedirect(isLoggedIn: boolean): void {
  const authenticationContext = getApplicationContext().authentication;
  const params = new URLSearchParams(window.location.search);
  const originalRedirect = params.get('origin');
  const challengeCode = params.get('challenge_code');

  if (!isLoggedIn) {
    window.location.replace(
      `/${paths.LOGIN}${buildLoginRedirectUrl(originalRedirect, params)}`,
    );
    return;
  }

  if (!originalRedirect || !challengeCode) {
    window.location.replace('/');
    return;
  }

  if (!isValidRedirectDomain(originalRedirect, authenticationContext)) {
    window.location.replace('/');
    return;
  }

  authenticationContext
    .generateAuthCode?.(challengeCode)
    .then((authCode) => {
      const redirectUrl = new URL(originalRedirect);
      redirectUrl.searchParams.set('authCode', authCode);
      const region = authenticationContext.serviceCountryStorage.get();
      if (region) {
        redirectUrl.searchParams.set('authRegion', region);
      }
      window.location.replace(redirectUrl.toString());
    })
    .catch(() => {
      const redirectUrl = new URL(originalRedirect);
      redirectUrl.searchParams.set('authError', 'true');
      window.location.replace(redirectUrl.toString());
    });
}
